if (!customElements.get('byte-review-spotlight')) {
  class ByteReviewSpotlight extends HTMLElement {
    constructor() {
      super();

      this.swiperInstance = null;
      this.boundHandleSectionLoad = this.handleSectionLoad.bind(this);
      this.boundHandleDomReady = this.handleDomReady.bind(this);
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();
      this.#scheduleInit();
      requestAnimationFrame(() => this.#markOverflowingComments());
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.#destroySwiper();
    }

    handleSectionLoad(event) {
      if (!event.target.contains(this)) return;

      this.#destroySwiper();
      this.#initSwiper();
    }

    handleDomReady() {
      this.#initSwiper();
      requestAnimationFrame(() => this.#markOverflowingComments());
    }

    handleResize() {
      this.#markOverflowingComments();
    }

    handleClick(event) {
      const toggleTrigger = event.target.closest('[data-review-toggle]');
      if (!toggleTrigger) return;

      const card = toggleTrigger.closest('[data-review-card]');
      if (!card) return;

      const isExpanded = card.classList.toggle('is-expanded');
      toggleTrigger.textContent = isExpanded ? toggleTrigger.dataset.collapseLabel : toggleTrigger.dataset.expandLabel;
    }

    #cacheElements() {
      this.swiperContainer = this.querySelector('[data-review-swiper]');
      this.prevButton = this.querySelector('[data-review-prev]');
      this.nextButton = this.querySelector('[data-review-next]');
      this.commentElements = this.querySelectorAll('.review-spotlight__comment');
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      window.addEventListener('resize', this.boundHandleResize);

      if (!window.Shopify?.designMode) return;

      document.addEventListener('shopify:section:load', this.boundHandleSectionLoad);
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      window.removeEventListener('resize', this.boundHandleResize);
      document.removeEventListener('shopify:section:load', this.boundHandleSectionLoad);
      document.removeEventListener('DOMContentLoaded', this.boundHandleDomReady);
    }

    #markOverflowingComments() {
      this.commentElements.forEach((comment) => {
        const card = comment.closest('[data-review-card]');
        if (!card || card.classList.contains('is-expanded')) return;

        const toggle = card.querySelector('[data-review-toggle]');
        if (!toggle) return;

        const isOverflowing = comment.scrollHeight > comment.clientHeight + 1;
        toggle.hidden = !isOverflowing;
      });
    }

    #scheduleInit() {
      /*
       * The Swiper vendor bundle may be declared after this component's own
       * script tag in the document, so `Swiper` can still be undefined the
       * moment this deferred script runs. Waiting for DOMContentLoaded
       * guarantees every deferred script (including the vendor bundle) has
       * already executed, regardless of tag order in theme.liquid.
       */
      if (document.readyState === 'complete') {
        this.#initSwiper();
        return;
      }

      document.addEventListener('DOMContentLoaded', this.boundHandleDomReady, { once: true });
    }

    #initSwiper() {
      if (!this.swiperContainer) return;

      if (typeof Swiper === 'undefined') {
        console.error('Swiper library not found. Load vendor-swiper-bundle before byte-review-spotlight.js.');
        return;
      }

      const slidesPerViewDesktop = Number(this.dataset.slidesPerView) || 3;
      const shouldAutoplay = this.dataset.autoplay === 'true';
      const autoplaySpeed = Number(this.dataset.autoplaySpeed) || 5000;

      this.swiperInstance = new Swiper(this.swiperContainer, {
        slidesPerView: 1.15,
        spaceBetween: 16,
        watchOverflow: true,
        navigation: {
          prevEl: this.prevButton,
          nextEl: this.nextButton
        },
        on: {
          lock: () => this.classList.add('is-locked'),
          unlock: () => this.classList.remove('is-locked')
        },
        autoplay: shouldAutoplay ? { delay: autoplaySpeed, disableOnInteraction: false } : false,
        breakpoints: {
          600: {
            slidesPerView: 2.15
          },
          990: {
            slidesPerView: slidesPerViewDesktop + 0.35
          }
        }
      });

      this.classList.toggle('is-locked', this.swiperInstance.isLocked);
    }

    #destroySwiper() {
      if (!this.swiperInstance) return;

      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  customElements.define('byte-review-spotlight', ByteReviewSpotlight);
}